import { Injectable, inject } from '@angular/core';

import { filter, map, mergeMap, of } from 'rxjs';

import { CamBaseService, GraphEndpoint, HandleSimpleRequest } from '@camelot/server';
import { CamUsersService } from '@camelot/user';
import { getUniqueArray, isNonNullable } from '@camelot/utils';

import {
  CalendarTimeEntry,
  CalendarTimeEntryCreationPayloadInput,
  CalendarTimeEntryExtended,
  CalendarTimeEntryModifierPayloadInput,
  calendarTimeEntryProps,
} from './dto/calendar-time-entry';
import { CREATE_TIME_ENTRY, UPDATE_TIME_ENTRY } from './mutations';
import { GET_CALENDAR_TIME_ENTRIES } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'planningService',
  endpoint: 'planning',
};

@Injectable({
  providedIn: 'root',
})
export class CamTimeEntriesService extends CamBaseService {
  public timeEntries = new HandleSimpleRequest<CalendarTimeEntry[]>();

  private _usersService = inject(CamUsersService);

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchTimeEntries$() {
    return this.timeEntries.fetch(
      this._graphService
        .fetchPagedQueryList<CalendarTimeEntry>(
          GET_CALENDAR_TIME_ENTRIES(
            ``,
            `
                ${calendarTimeEntryProps.get('id')}
                ${calendarTimeEntryProps.get('start')}
                ${calendarTimeEntryProps.get('end')}
                ${calendarTimeEntryProps.get('title')}
                ${calendarTimeEntryProps.get('visitId')}
                ${calendarTimeEntryProps.get('missionId')}
                ${calendarTimeEntryProps.get('unavailabilityId')}
                ${calendarTimeEntryProps.get('userId')}
                ${calendarTimeEntryProps.get('validated')}
              `
          ),
          'timeEntries',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  protected _fetchTimeEntry$(id: string) {
    return this._graphService
      .fetchPagedQueryList<CalendarTimeEntryExtended>(
        GET_CALENDAR_TIME_ENTRIES(
          `where: { id: { eq: "${id}" } }`,
          `
                ${calendarTimeEntryProps.get('id')}
                ${calendarTimeEntryProps.get('start')}
                ${calendarTimeEntryProps.get('end')}
                ${calendarTimeEntryProps.get('title')}
                ${calendarTimeEntryProps.get('visitId')}
                ${calendarTimeEntryProps.get('missionId')}
                ${calendarTimeEntryProps.get('unavailabilityId')}
                ${calendarTimeEntryProps.get('userId')}
                ${calendarTimeEntryProps.get('validated')}
              `
        ),
        'timeEntries',
        graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        mergeMap(entities => this._enrichWithUsers(entities))
      );
  }

  protected _fetchTimeEntriesByMission$(missionId: string) {
    return this._graphService
      .fetchPagedQueryList<CalendarTimeEntry>(
        GET_CALENDAR_TIME_ENTRIES(
          `where: { missionId: { eq: "${missionId}" } }`,
          `
                ${calendarTimeEntryProps.get('id')}
                ${calendarTimeEntryProps.get('start')}
                ${calendarTimeEntryProps.get('end')}
                ${calendarTimeEntryProps.get('title')}
                ${calendarTimeEntryProps.get('visitId')}
                ${calendarTimeEntryProps.get('missionId')}
                ${calendarTimeEntryProps.get('unavailabilityId')}
                ${calendarTimeEntryProps.get('userId')}
                ${calendarTimeEntryProps.get('validated')}
              `
        ),
        'timeEntries',
        graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        mergeMap(entities => this._enrichWithUsers(entities))
      );
  }

  public createCalendarTimeEntry$(timeEntry: CalendarTimeEntryCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<CalendarTimeEntry>(CREATE_TIME_ENTRY(timeEntry), graphEndpoint.clientName, ['timeEntries'])
      .pipe(mergeMap(() => this.fetchTimeEntries$()));
  }
  public updateCalendarTimeEntry$(timeEntry: Partial<CalendarTimeEntryModifierPayloadInput>) {
    return this._graphService.mutateBuilder<CalendarTimeEntry>(UPDATE_TIME_ENTRY(timeEntry), graphEndpoint.clientName, [
      'timeEntries',
    ]);
  }

  private _enrichWithUsers(entities: { items?: CalendarTimeEntry[] }) {
    if (entities.items?.length === 0) {
      return of(entities);
    }

    const ids = getUniqueArray((entities.items ?? []).map(e => e.userId ?? null)).filter(isNonNullable);
    return this._usersService.fetchUsersByIds$(ids).pipe(
      map(data => ({
        ...entities,
        items: (entities.items ?? []).map<CalendarTimeEntryExtended>(e => ({
          ...e,
          ...{ user: data.find(c => c.id === e.userId) },
        })),
      }))
    );
  }
}
